/**
 * AlertAcknowledgeModal
 *
 * Modal for acknowledging a security alert with investigation notes.
 */

import { useState } from 'react';
import { X, Shield, Loader2 } from 'lucide-react';
import { useAcknowledgeAlert } from '../../hooks/useSecurity';
import type { SecurityAlert } from '../../types/security.types';

interface AlertAcknowledgeModalProps {
  alert: SecurityAlert;
  onClose: () => void;
}

export function AlertAcknowledgeModal({ alert, onClose }: AlertAcknowledgeModalProps) {
  const [notes, setNotes] = useState('');
  const acknowledgeMutation = useAcknowledgeAlert();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await acknowledgeMutation.mutateAsync({ alertId: alert.alert_id, notes });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-ems-border">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-ems-green" />
            <h3 className="text-sm font-semibold text-ems-text-primary">Acknowledge Alert</h3>
          </div>
          <button onClick={onClose} className="text-ems-text-muted hover:text-ems-text-secondary">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Alert Info */}
        <div className="px-5 py-3 bg-ems-bg-card-hover border-b border-ems-border">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              alert.severity === 'critical' ? 'bg-ems-red/10 text-ems-red' :
              alert.severity === 'high' ? 'bg-ems-amber/10 text-ems-amber' :
              alert.severity === 'medium' ? 'bg-ems-amber/10 text-ems-amber' :
              'bg-ems-sky/10 text-ems-sky'
            }`}>
              {alert.severity}
            </span>
            <span className="text-xs text-ems-text-muted">{alert.category}</span>
          </div>
          <p className="text-sm font-medium text-ems-text-primary">{alert.title}</p>
          {alert.description && (
            <p className="text-xs text-ems-text-secondary mt-1">{alert.description}</p>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-5 py-4">
          <label className="block text-xs font-semibold text-ems-text-muted uppercase mb-1.5">
            Investigation Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-ems-border rounded-lg text-sm bg-ems-bg text-ems-text-primary focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green resize-none"
            placeholder="Add investigation notes..."
          />

          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-ems-text-secondary border border-ems-border rounded-lg hover:bg-ems-bg-card-hover"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={acknowledgeMutation.isPending}
              className="px-4 py-2 text-sm text-ems-green bg-ems-forest border border-ems-green/20 rounded-lg hover:bg-ems-forest/80 disabled:opacity-50 flex items-center gap-1.5"
            >
              {acknowledgeMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Acknowledge
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
